import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { TranslatePipe } from './i18n.service';
import { ApiService, showToast } from './api.service';
import { LogoComponent } from './logo.component';
import { aucklandHour } from './auckland-time';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, TranslatePipe, LogoComponent],
  template: `
    <div class="min-h-screen bg-slate-100 lg:flex">
      <aside class="hidden lg:flex w-[236px] min-h-screen bg-[#102C57] text-white border-r border-white/10 flex-col fixed inset-y-0 left-0 z-40">
        <div class="h-[72px] px-5 flex items-center border-b border-white/10">
          <app-logo theme="light"></app-logo>
        </div>

        <nav class="flex-1 py-4 px-3 space-y-1">
          <a routerLink="/" routerLinkActive="desktop-active" [routerLinkActiveOptions]="{exact: true}"
             class="desktop-nav-link">
            <i class="ti ti-home text-[18px]"></i>
            <span>{{ 'home' | t }}</span>
          </a>
          <a routerLink="/schedule" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-calendar text-[18px]"></i>
            <span>{{ 'schedule' | t }}</span>
          </a>
          <a routerLink="/create-job" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-plus text-[18px]"></i>
            <span>New Job</span>
          </a>
          <a routerLink="/inventory" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-box text-[18px]"></i>
            <span>{{ 'materials' | t }}</span>
          </a>
          <a routerLink="/purchase-orders" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-clipboard-list text-[18px]"></i>
            <span>Purchase Orders</span>
          </a>
          @if (isDrainlayer()) {
            <a routerLink="/as-built" routerLinkActive="desktop-active" class="desktop-nav-link">
              <i class="ti ti-ruler-measure text-[18px]"></i>
              <span>As Built</span>
            </a>
          }
          <a routerLink="/leave-requests" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-calendar-plus text-[18px]"></i>
            <span>Leave</span>
          </a>
          <a routerLink="/notifications" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-bell text-[18px]"></i>
            <span>Notifications</span>
          </a>
          <a routerLink="/profile" routerLinkActive="desktop-active"
             class="desktop-nav-link">
            <i class="ti ti-user text-[18px]"></i>
            <span>{{ 'me' | t }}</span>
          </a>
        </nav>

        <div class="p-4 border-t border-white/10">
          <div class="flex items-center gap-3 min-w-0">
            <div class="w-9 h-9 rounded-full bg-[#2B7BE4] flex items-center justify-center text-xs font-bold overflow-hidden shrink-0">
              @if (profilePic()) {
                <img [src]="profilePic()" alt="Avatar" class="w-full h-full object-cover" referrerpolicy="no-referrer">
              } @else {
                {{ userInitials() }}
              }
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold truncate">{{ userName() }}</p>
              <p class="text-[11px] text-white/45 truncate">{{ userRole() }}</p>
            </div>
          </div>
        </div>
      </aside>

      <div class="min-h-screen min-w-0 flex-1 lg:pl-[236px]">
        <header class="hidden lg:flex h-[72px] bg-[#102C57] text-white items-center justify-between px-7 sticky top-0 z-30 border-b border-white/10">
          <div class="min-w-0">
            <p class="text-[11px] text-white/45 leading-none mb-1">Good {{ timeOfDay() }}</p>
            <h1 class="text-lg font-semibold truncate">{{ firstName() }}</h1>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-[11px] text-white/45">{{ shiftStatusLabel() }}</span>
            <button type="button"
                    (click)="openShiftDialog()"
                    [disabled]="shiftUpdating()"
                    class="h-9 px-4 rounded-full border text-white text-xs font-bold flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    [class.bg-emerald-500]="!shiftActive()"
                    [class.border-emerald-400]="!shiftActive()"
                    [class.hover:bg-emerald-400]="!shiftActive()"
                    [class.bg-red-500]="shiftActive()"
                    [class.border-red-400]="shiftActive()"
                    [class.hover:bg-red-400]="shiftActive()"
                    [attr.aria-busy]="shiftUpdating()">
              @if (shiftUpdating()) {
                <i class="ti ti-loader-2 animate-spin"></i>
              } @else {
                <i [class]="shiftActive() ? 'ti ti-player-stop-filled' : 'ti ti-player-play-filled'"></i>
              }
              {{ shiftButtonLabel() }}
            </button>
            <button (click)="openCreateJob()"
                    class="h-9 px-4 rounded-full bg-white/10 border border-white/10 text-white text-xs font-bold flex items-center gap-2 hover:bg-white/15">
              <i class="ti ti-plus"></i>
              New Job
            </button>
            <button routerLink="/notifications" class="w-9 h-9 rounded-full bg-white/10 border border-white/10 shrink-0 flex items-center justify-center text-white/90">
              <i class="ti ti-bell text-[18px]"></i>
            </button>
            <button routerLink="/profile" class="w-9 h-9 rounded-full bg-[#2B7BE4] border border-white/15 shrink-0 flex items-center justify-center overflow-hidden text-xs font-bold text-white">
              @if (profilePic()) {
                <img [src]="profilePic()" alt="Avatar" class="w-full h-full object-cover" referrerpolicy="no-referrer">
              } @else {
                {{ userInitials() }}
              }
            </button>
          </div>
        </header>

        @if (showShiftConfirm()) {
          <div class="fixed inset-0 z-[90] flex items-center justify-center p-4" style="background:rgba(15,23,42,0.52);">
            <div class="w-full max-w-[380px] rounded-[28px] bg-white border border-slate-100 shadow-2xl p-5 text-slate-900">
              <div class="rounded-3xl p-5 text-white text-center" style="background:#173F73;">
                <p class="text-[11px] font-semibold text-white/55 uppercase tracking-widest">Current Shift</p>
                <p class="font-mono text-[42px] leading-none font-bold mt-3">{{ shiftElapsedDisplay() }}</p>
                <p class="text-xs text-white/55 mt-3">Started {{ formatShiftTime(shiftStartedAt()) }}</p>
              </div>

              @if (shiftTimeReviewMessage()) {
                <div class="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-amber-900">
                  <i class="ti ti-clock-exclamation mt-0.5"></i>
                  <div>
                    <p class="text-sm font-bold">Check this shift time</p>
                    <p class="text-xs mt-0.5">{{ shiftTimeReviewMessage() }}</p>
                  </div>
                </div>
              }

              @if (blockingWork().length) {
                <div class="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                  <p class="text-sm font-bold text-amber-900">Your shift cannot end yet</p>
                  <p class="text-xs text-amber-800 mt-0.5">Pause or stop your active timer below first.</p>
                  <div class="space-y-2 mt-3">
                    @for (item of blockingWork(); track item.type + '-' + item.id + '-' + item.session_id) {
                      <div class="rounded-xl border border-amber-200 bg-white px-3 py-2 flex items-center gap-3">
                        <div class="min-w-0 flex-1">
                          <p class="text-[10px] font-bold uppercase text-amber-700">{{ item.type }} · {{ item.status }}</p>
                          <p class="text-sm font-semibold truncate">{{ item.title || item.id }}</p>
                          <p class="text-[11px] text-slate-500">{{ item.id }}</p>
                        </div>
                        <a [routerLink]="blockingWorkRoute(item)" (click)="closeShiftDialog()" class="h-9 px-3 rounded-lg bg-amber-600 text-white text-xs font-bold flex items-center gap-1.5"><i class="ti ti-external-link"></i> Open</a>
                      </div>
                    }
                  </div>
                </div>
              }

              <div class="grid grid-cols-2 gap-3 mt-5">
                <button type="button" (click)="closeShiftDialog()" [disabled]="shiftUpdating()" class="h-12 rounded-2xl bg-slate-50 border border-slate-100 text-slate-600 text-sm font-bold">Close</button>
                <button type="button" (click)="confirmStopShift()" [disabled]="shiftUpdating() || blockingWork().length > 0" class="h-12 rounded-2xl bg-red-500 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                  @if (shiftUpdating()) { <i class="ti ti-loader-2 animate-spin"></i> } @else { <i class="ti ti-player-stop-filled"></i> Stop Shift }
                </button>
              </div>
            </div>
          </div>
        }

        <router-outlet></router-outlet>
      </div>

      <button (click)="openCreateJob()"
        class="lg:hidden fixed bottom-[18px] left-1/2 -translate-x-1/2 w-[56px] h-[56px] text-white rounded-full flex items-center justify-center shadow-[0_10px_22px_rgba(0,132,255,0.35)] z-50 active:scale-95 transition-all"
        style="background:#0A84FF;"
        aria-label="Create job">
        <i class="ti ti-plus text-[32px]"></i>
      </button>

      <div class="lg:hidden fixed bottom-0 left-0 right-0 z-40 pointer-events-none flex justify-center">
        <nav class="w-full max-w-[560px] bg-white/95 backdrop-blur-2xl rounded-t-[28px] border-t border-slate-100 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] pb-safe px-3 h-[70px] grid grid-cols-5 items-center pointer-events-auto">
          <a routerLink="/" routerLinkActive="active-tab" [routerLinkActiveOptions]="{exact: true}" #rlaHome="routerLinkActive"
             class="h-14 flex flex-col items-center justify-center gap-1 transition-colors"
             [class.text-slate-950]="rlaHome.isActive"
             [class.text-slate-400]="!rlaHome.isActive">
            <i class="ti ti-home text-[20px]"></i>
            <span class="text-[9px] font-semibold leading-none">{{ 'home' | t }}</span>
          </a>

          <a routerLink="/schedule" routerLinkActive="active-tab" #rlaSchedule="routerLinkActive"
             class="h-14 flex flex-col items-center justify-center gap-1 transition-colors"
             [class.text-slate-950]="rlaSchedule.isActive"
             [class.text-slate-400]="!rlaSchedule.isActive">
            <i class="ti ti-calendar text-[20px]"></i>
            <span class="text-[9px] font-semibold leading-none">{{ 'schedule' | t }}</span>
          </a>

          <span class="h-14" aria-hidden="true"></span>

          <a routerLink="/inventory" routerLinkActive="active-tab" #rlaMaterials="routerLinkActive"
             class="h-14 flex flex-col items-center justify-center gap-1 transition-colors"
             [class.text-slate-950]="rlaMaterials.isActive"
             [class.text-slate-400]="!rlaMaterials.isActive">
            <i class="ti ti-box text-[20px]"></i>
            <span class="text-[9px] font-semibold leading-none">{{ 'materials' | t }}</span>
          </a>

          <a routerLink="/purchase-orders" routerLinkActive="active-tab" #rlaPo="routerLinkActive"
             class="h-14 flex flex-col items-center justify-center gap-1 transition-colors"
             [class.text-slate-950]="rlaPo.isActive"
             [class.text-slate-400]="!rlaPo.isActive">
            <i class="ti ti-clipboard-list text-[20px]"></i>
            <span class="text-[9px] font-semibold leading-none">PO</span>
          </a>
        </nav>
      </div>
    </div>
  `
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private api = inject(ApiService);

  shiftActive = signal(false);
  shiftUpdating = signal(false);
  shiftStartedAt = signal<Date | null>(null);
  shiftEndedAt = signal<Date | null>(null);
  blockingWork = signal<any[]>([]);
  shiftTimeReviewMessage = signal('');
  showShiftConfirm = signal(false);
  shiftTick = signal(0);
  private shiftTimerId: ReturnType<typeof setInterval> | undefined;

  currentUser = () => this.api.currentUser() || {};
  userName = () => this.currentUser().name || 'Staff';
  userRole = () => this.currentUser().role || 'Worker';
  isDrainlayer = () => String(this.currentUser().role || '') === 'Drainlayer';
  profilePic = () => this.currentUser().profile_photo_url || '';
  firstName = () => this.userName().trim().split(/\s+/)[0] || 'Staff';
  userInitials = () => {
    const parts = this.userName().trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : (parts[0]?.slice(0, 2) || 'S')).toUpperCase();
  };
  timeOfDay = () => {
    const hour = aucklandHour();
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
  };
  shiftButtonLabel = () => this.shiftActive() ? 'Stop Shift' : 'Start Shift';
  shiftStatusLabel = () => {
    if (this.shiftActive()) return `Started ${this.formatShiftTime(this.shiftStartedAt())}`;
    if (this.shiftEndedAt()) return `Ended ${this.formatShiftTime(this.shiftEndedAt())}`;
    return 'Not started';
  };

  ngOnInit() {
    this.loadShiftStatus();
    this.shiftTimerId = setInterval(() => this.shiftTick.update(value => value + 1), 1000);
  }

  ngOnDestroy() {
    if (this.shiftTimerId) clearInterval(this.shiftTimerId);
  }

  openCreateJob() {
    this.router.navigate(['/create-job']);
  }

  openShiftDialog() {
    if (this.shiftUpdating()) return;

    if (!this.shiftActive()) {
      this.toggleShift('start');
      return;
    }

    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) this.applyShift(res.data);
        this.showShiftConfirm.set(true);
      },
      error: () => {
        this.showShiftConfirm.set(true);
      }
    });
  }

  closeShiftDialog() {
    if (!this.shiftUpdating()) this.showShiftConfirm.set(false);
  }

  confirmStopShift() {
    if (this.blockingWork().length) {
      showToast('error', 'Pause or stop your active work timer before ending your shift.');
      return;
    }
    this.toggleShift('end');
  }

  blockingWorkRoute(item: any): any[] {
    return [item?.type === 'Project' ? '/project' : '/job', String(item?.id || '')];
  }

  private toggleShift(action: 'start' | 'end') {
    if (this.shiftUpdating()) return;

    this.shiftUpdating.set(true);
    this.api.updateShift(action).subscribe({
      next: (res: any) => {
        this.shiftUpdating.set(false);
        if (res.success && res.data) {
          this.applyShift(res.data);
          this.showShiftConfirm.set(false);
          showToast('success', action === 'start' ? 'Shift started.' : 'Shift ended.');
        } else {
          showToast('error', res.message || 'Unable to update shift.');
        }
      },
      error: (err: any) => {
        this.shiftUpdating.set(false);
        const blockers = err?.error?.data?.blocking_work;
        if (Array.isArray(blockers)) {
          this.blockingWork.set(blockers);
          this.showShiftConfirm.set(true);
        }
        showToast('error', err?.error?.message || 'Unable to update shift.');
      }
    });
  }

  private loadShiftStatus() {
    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        if (res.success && res.data) this.applyShift(res.data);
      }
    });
  }

  private applyShift(data: any) {
    const sessions = Array.isArray(data.today_shifts) ? data.today_shifts : [];
    const latestSession = sessions.slice().reverse().find((shift: any) => shift?.started_at) || null;
    const latestEndedSession = sessions.slice().reverse().find((shift: any) => shift?.ended_at) || null;
    const active = !!data.active;

    this.shiftActive.set(active);
    this.blockingWork.set(Array.isArray(data.blocking_work) ? data.blocking_work : []);
    this.shiftTimeReviewMessage.set(data.requires_time_review ? String(data.time_review_message || '') : '');
    this.shiftStartedAt.set(this.parseDate(data.started_at) || (active ? this.parseDate(latestSession?.started_at) : null));
    this.shiftEndedAt.set(this.parseDate(data.ended_at || data.last_ended_at) || this.parseDate(latestEndedSession?.ended_at));
  }

  private parseDate(value: any): Date | null {
    if (!value) return null;
    const normalized = String(value).includes('T') ? String(value) : String(value).replace(' ', 'T');
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  formatShiftTime(date: Date | null): string {
    if (!date) return '--';
    return date.toLocaleTimeString('en-NZ', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Pacific/Auckland'
    });
  }

  shiftElapsedDisplay(): string {
    this.shiftTick();
    const startedAt = this.shiftStartedAt();
    const seconds = startedAt ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)) : 0;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remaining = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
  }
}
